import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { marketingService } from '../../../services/marketingService';

const CommunicationCampaigns = ({ expanded = false }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadCampaigns();
  }, []);

  const loadCampaigns = async () => {
    try {
      setLoading(true);
      const data = await marketingService?.getCampaigns();
      setCampaigns(data);
    } catch (err) {
      setError(err?.message || 'Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  const campaignStats = {
    active: campaigns?.filter(c => c?.status === 'active')?.length || 0,
    scheduled: campaigns?.filter(c => c?.status === 'scheduled')?.length || 0,
    completed: campaigns?.filter(c => c?.status === 'completed')?.length || 0,
    draft: campaigns?.filter(c => c?.status === 'draft')?.length || 0
  };

  const totalSent = campaigns?.reduce((sum, c) => sum + (c?.sentCount || 0), 0);
  const totalDelivered = campaigns?.reduce((sum, c) => sum + (c?.deliveredCount || 0), 0);
  const totalOpened = campaigns?.reduce((sum, c) => sum + (c?.openedCount || 0), 0);
  const totalConversions = campaigns?.reduce((sum, c) => sum + (c?.conversionCount || 0), 0);

  const performanceMetrics = [
    { label: 'Sent', value: totalSent, icon: 'Send', color: 'text-primary' },
    { label: 'Delivered', value: totalDelivered, icon: 'CheckCircle', color: 'text-success' },
    { label: 'Opened', value: totalOpened, icon: 'Mail', color: 'text-warning' },
    { label: 'Conversions', value: totalConversions, icon: 'TrendingUp', color: 'text-muted-foreground' }
  ];

  const displayCampaigns = expanded ? campaigns : campaigns?.slice(0, 4);

  return (
    <div className="bg-card rounded-xl border border-border shadow-elevation-2 p-4 md:p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg md:text-xl font-semibold text-foreground flex items-center gap-2">
          <Icon name="Megaphone" size={20} className="text-primary" />
          Marketing Campaigns
        </h2>
        <Button variant="outline" size="sm" iconName="Plus" iconPosition="left">
          New Campaign
        </Button>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <div className="bg-success/10 rounded-lg p-3 border border-success/20">
          <p className="text-xs text-success mb-1">Active</p>
          <p className="text-xl font-bold text-success">{campaignStats?.active}</p>
        </div>
        <div className="bg-warning/10 rounded-lg p-3 border border-warning/20">
          <p className="text-xs text-warning mb-1">Scheduled</p>
          <p className="text-xl font-bold text-warning">{campaignStats?.scheduled}</p>
        </div>
        <div className="bg-primary/10 rounded-lg p-3 border border-primary/20">
          <p className="text-xs text-primary mb-1">Completed</p>
          <p className="text-xl font-bold text-primary">{campaignStats?.completed}</p>
        </div>
        <div className="bg-muted/50 rounded-lg p-3 border border-border">
          <p className="text-xs text-muted-foreground mb-1">Draft</p>
          <p className="text-xl font-bold text-foreground">{campaignStats?.draft}</p>
        </div>
      </div>
      <div className="mb-6">
        <h3 className="text-sm font-semibold text-foreground mb-3">Campaign Performance</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {performanceMetrics?.map((metric, index) => (
            <div key={index} className="bg-muted/50 rounded-lg p-3 border border-border text-center">
              <Icon name={metric?.icon} size={20} className={`mx-auto mb-2 ${metric?.color}`} />
              <p className="text-xs text-muted-foreground mb-1">{metric?.label}</p>
              <p className="text-lg font-bold text-foreground">{metric?.value}</p>
            </div>
          ))}
        </div>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : error ? (
        <div className="bg-error/10 border border-error rounded-lg p-3">
          <p className="text-error text-sm">{error}</p>
        </div>
      ) : (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-foreground">Active Campaigns</h3>
          {displayCampaigns?.map((campaign) => {
            const openRate = campaign?.sentCount > 0 ? ((campaign?.openedCount / campaign?.sentCount) * 100)?.toFixed(1) : 0;
            const conversionRate = campaign?.sentCount > 0 ? ((campaign?.conversionCount / campaign?.sentCount) * 100)?.toFixed(1) : 0;

            return (
              <div key={campaign?.id} className="bg-muted/30 rounded-lg p-3 border border-border hover:border-primary transition-colors">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <p className="font-semibold text-foreground">{campaign?.campaignName}</p>
                    <p className="text-xs text-muted-foreground">{campaign?.campaignType} • {campaign?.targetAudience}</p>
                  </div>
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      campaign?.status === 'active' ?'bg-success/10 text-success'
                        : campaign?.status === 'scheduled' ?'bg-warning/10 text-warning'
                        : campaign?.status === 'completed' ?'bg-primary/10 text-primary' :'bg-muted text-muted-foreground'
                    }`}
                  >
                    {campaign?.status}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2 mt-3">
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground">Sent</p>
                    <p className="text-sm font-semibold text-foreground">{campaign?.sentCount}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground">Open Rate</p>
                    <p className="text-sm font-semibold text-foreground">{openRate}%</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground">Conversion</p>
                    <p className="text-sm font-semibold text-foreground">{conversionRate}%</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-3 text-xs text-muted-foreground">
                  <Icon name={campaign?.channel === 'sms' ? 'MessageSquare' : campaign?.channel === 'email' ? 'Mail' : 'Phone'} size={12} />
                  <span>{campaign?.channel?.toUpperCase()}</span>
                  <span>•</span>
                  <span>Budget: ${campaign?.budget} / Spent: ${campaign?.spent}</span>
                </div>
              </div>
            );
          })}
          {!expanded && campaigns?.length > 4 && (
            <Button variant="ghost" size="sm" fullWidth>
              View All Campaigns ({campaigns?.length})
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default CommunicationCampaigns;